import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'completed',
    'notApplicable',
    'total',
    'percentage',
    'progress',
    'bar',
  ]
  static values = {
    selector: String,
  }

  declare readonly hasBarTarget: boolean
  declare readonly hasPercentageTarget: boolean
  declare readonly hasProgressTarget: boolean
  declare readonly hasNotApplicableTarget: boolean

  private completedTarget!: HTMLElement
  private notApplicableTarget?: HTMLElement
  private totalTarget!: HTMLElement
  private percentageTarget?: HTMLElement
  private progressTarget?: HTMLElement
  private barTarget?: HTMLElement
  private selectorValue!: string

  connect(): void {
    this.initCounts()
    this.updateProgress()
    this.addCustomEventListeners()
  }

  private addCustomEventListeners(): void {
    // Listen for the update event from toggle.js
    this.element.addEventListener(
      'ledger-check-list--progress:updateSummaryUser',
      ((event: CustomEvent<{ completed: boolean }>) => {
        this.updateCompletedCount(event.detail.completed)
        this.updateProgressBar()
      }) as EventListener
    )

    this.element.addEventListener(
      'ledger-check-list--progress:updateSummary',
      ((_event: CustomEvent) => {
        this.initCounts()
        this.updateProgress()
      }) as EventListener
    )
  }

  private initCounts(): void {
    const completedCount = this.getCompletedCount()
    const notApplicableCount = this.getNotApplicableCount()
    const totalCount = this.getTotalCount()

    if (this.hasNotApplicableTarget && this.notApplicableTarget) {
      this.notApplicableTarget.textContent = notApplicableCount.toString()
      this.completedTarget.textContent = completedCount.toString()
    } else {
      this.completedTarget.textContent = Math.min(
        completedCount + notApplicableCount,
        totalCount
      ).toString()
    }
  }

  private getNotApplicableCount(): number {
    return document.querySelectorAll<HTMLElement>(
      `${this.selectorValue}[data-not-applicable="true"]`
    ).length
  }

  private getCompletedCount(): number {
    return document.querySelectorAll<HTMLElement>(
      `${this.selectorValue}[data-completed="true"][data-not-applicable="false"]`
    ).length
  }

  private getTotalCount(): number {
    return parseInt(this.totalTarget.textContent || '1', 10)
  }

  private calculateProgress(): number {
    const completedCount = this.getCompletedCount()
    const notApplicableCount = this.getNotApplicableCount()
    const totalCount = this.getTotalCount()
    const effectiveCompleted = Math.min(
      totalCount,
      completedCount + notApplicableCount
    )

    return Math.round((effectiveCompleted / totalCount) * 100)
  }

  // Update the completed count based on whether we're adding or subtracting
  private updateCompletedCount(completed: boolean): void {
    let completedCount = parseInt(this.completedTarget.textContent || '0', 10)

    // If adding to count, increment; otherwise decrement (but not below 0)
    completedCount = completed
      ? completedCount + 1
      : Math.max(0, completedCount - 1)

    this.completedTarget.textContent = completedCount.toString()
  }

  private updateProgress(): void {
    this.updatePercentage()
    this.updateProgressBar()
  }

  private updatePercentage(): void {
    if (!this.hasPercentageTarget || !this.percentageTarget) {
      return
    }

    const percentage = this.calculateProgress()

    if (this.percentageTarget) {
      this.percentageTarget.textContent = `${percentage}%`
    }
  }

  private updateProgressBar(): void {
    if (!this.hasProgressTarget || !this.hasBarTarget) {
      return
    }

    const percentage = this.calculateProgress()
    const progressClass = this.calculateProgressClass(percentage)

    if (this.barTarget) {
      this.barTarget.style.width = `${percentage}%`
    }

    if (this.progressTarget) {
      this.progressTarget.className = `progress ${progressClass}`
    }
  }

  private calculateProgressClass(percentage: number): string {
    if (percentage < 31) {
      return 'progress-danger'
    } else if (percentage < 71) {
      return 'progress-warning'
    }

    return 'progress-success'
  }
}
